import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from "react-router-dom";

import { Card, Container, CardBody, CardHeader, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import {
  Paperclip, User, UserCheck, Calendar, Clock, Flag, AlertCircle,
  MessageSquare, Send, FileText
} from 'react-feather';
import Dropzone from 'react-dropzone-uploader';
import axios from 'axios';

import Breadcrumb from '../../../Common/BreadCrumb/BreadCrumbs.component';
import SpinnerLoader from '../../../Common/LoadingAnimation/SpinnerLoader.component';

import useOrgIdentifer from '@hooks/org_identifier';

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#84cc16'];

const initialsOf = (name) =>
  (name || '?').split(/\s+/).filter(Boolean).map(s => s[0]).slice(0, 2).join('').toUpperCase() || '?';

const colorFor = (name) => {
  const s = name || '';
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const THEME = { solid: '#0d6efd', tint: '#e7f1ff', dark: '#0a58ca' };

const SolidBadge = ({ children }) => (
  <span
    style={{
      background: THEME.solid,
      color: '#fff',
      fontSize: 12,
      padding: '6px 10px',
      borderRadius: 6,
      fontWeight: 500,
      display: 'inline-block',
    }}
  >
    {children}
  </span>
);

const TintBadge = ({ children, icon }) => (
  <span
    style={{
      background: THEME.tint,
      color: THEME.dark,
      fontSize: 12,
      padding: '6px 10px',
      borderRadius: 6,
      fontWeight: 500,
      display: 'inline-flex',
      alignItems: 'center',
      border: `1px solid ${THEME.solid}33`,
    }}
  >
    {icon}
    {children}
  </span>
);

const Avatar = ({ name, size = 32 }) => (
  <div
    className='d-flex align-items-center justify-content-center text-white fw-bold'
    style={{
      width: size, height: size, borderRadius: '50%',
      background: colorFor(name), fontSize: Math.round(size * 0.4),
      flexShrink: 0,
    }}
    title={name || ''}
  >
    {initialsOf(name)}
  </div>
);

const MetaItem = ({ icon, label, value }) => (
  <div className='d-flex align-items-start'>
    <div className='text-muted me-2' style={{ marginTop: 2 }}>{icon}</div>
    <div className='min-w-0'>
      <div className='text-muted' style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div className='text-dark fw-semibold text-truncate' style={{ fontSize: 13 }}>
        {value || '—'}
      </div>
    </div>
  </div>
);

export default function TicketDetails({ current_user, current_organization, take_my_org_details }) {

  const history = useHistory();
  const org_identifier = useOrgIdentifer();

  const [data, setData] = useState({});
  const [imageUpload, setImageUpload] = useState({});
  const [toggle, setToggle] = useState('');
  const [loading, setLoading] = useState(true);
  const [accessibleModules, setAccessibleModules] = useState();

  const replyText = useRef("");
  const { issue_id } = useParams();

  useEffect(() => {
    take_my_org_details(org_identifier);
  }, [org_identifier]);

  useEffect(() => {
    let accessible_modules = current_user?.part_of_org_list?.filter(x => x.identifier == org_identifier).map((y) => y?.accessible_modules).flat();
    setAccessibleModules(accessible_modules);
  }, [current_user]);

  useEffect(() => {
    axios.get(`/api/v1/organizations/${org_identifier}/issues/${issue_id}?page_type=client&controller_name=issues`).then(res => {
      setData(res.data?.issue);
      setLoading(false);
    }).catch(err => {
      setLoading(false);
      if (err?.response?.data?.page_auth_failed) {
        history.push("/");
        toast.error(err?.response?.data?.message);
      } else if (!err?.response?.data?.org_auth_failed) {
        toast.error(err?.response?.data?.message);
      }
    });
  }, [toggle]);

  const uploadFile = (meta, file) => {
    const body = new FormData();
    body.append('file', file);
    body.append('filename', meta.name);
    body.append('page_type', "client");
    body.append('controller_name', "issues");
    axios.post(`/api/v1/organizations/${org_identifier}/issues/upload_file`, body, {})
      .then(res => {
        toast.success(res.data?.message);
        setImageUpload({ ...res.data?.upload, content_type: 'image/png', filename: meta.name });
      }).catch(err => {
        toast.error(err.response.data?.message);
      });
  };

  const removeUploadedFile = (attachment_id) => {
    axios.delete(`/api/v1/organizations/${org_identifier}/issues/${attachment_id}/remove_file?page_type=client&controller_name=issues`)
      .then(res => {
        toast.success(res.data?.message);
        setImageUpload({});
      }).catch(err => {
        toast.error(err.response.data?.message);
      });
  };

  const handleChangeStatus = ({ meta, file }, status) => {
    if (status == 'done') uploadFile(meta, file);
    if (status == 'removed') removeUploadedFile(imageUpload?.id);
  };

  function handleUpload() {
    axios.put(`api/v1/organizations/${org_identifier}/issues/${issue_id}`, {
      issues: { uploads: [imageUpload] },
      page_type: "client",
      controller_name: "issues",
    }).then(res => {
      toast.success(res.data?.message);
      setImageUpload({});
      setToggle(curr => !curr);
    });
  }

  function handleSubmit() {
    axios.put(`/api/v1/organizations/${org_identifier}/issues/${issue_id}/reply_to_ticket`, {
      issues: { notes: replyText.current.value },
      page_type: "client",
      controller_name: "issues",
    }).then(res => {
      toast.success(res.data?.message);
      setToggle(curr => !curr);
    }).catch(err => {
      toast.error(err.response.data?.message);
    });
  }

  function gotoReply() {
    replyText.current.focus();
  }

  const canReply =
    ((current_user?.id && current_user?.is_admin) || accessibleModules?.includes("data_access")) && !data?.closed;

  return (
    <div>
      <Container fluid>
        <Breadcrumb
          parent={"Issues"}
          title={`Issue ${issue_id}`}
          childrenObj={[{ name: 'Issues', url: `/organizations/${org_identifier}/issues` }]}
        />

        <SpinnerLoader loading={loading && data?.id}>
          <Card className='shadow-sm border-0'>
            <CardHeader className='bg-white border-0 pt-4 px-4 pb-3'>
              <div className='d-flex justify-content-between align-items-start flex-wrap gap-3'>
                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                  <div className='d-flex align-items-center mb-2 flex-wrap' style={{ gap: 8 }}>
                    <span className='text-muted fw-bold' style={{ fontSize: 13 }}>#{data?.id}</span>
                    {data?.status_name && (
                      <SolidBadge>{data?.status_name}</SolidBadge>
                    )}
                    {data?.tracker_name && (
                      <TintBadge>{data?.tracker_name}</TintBadge>
                    )}
                    {data?.priority && (
                      <TintBadge icon={<Flag size={11} className='me-1' />}>
                        {data?.priority}
                      </TintBadge>
                    )}
                  </div>
                  <h3 className='fw-semibold mb-0 text-dark' style={{ lineHeight: 1.3 }}>
                    {data?.subject}
                  </h3>
                </div>
                {canReply && (
                  <button className='btn btn-success d-inline-flex align-items-center' onClick={gotoReply}>
                    <MessageSquare size={14} className='me-2' /> Reply
                  </button>
                )}
              </div>
            </CardHeader>

            <CardBody className='px-4 pt-0 pb-4'>
              <div className='bg-light rounded p-3 mb-4'>
                <Row className='g-3'>
                  <Col xs={6} md={4}>
                    <MetaItem icon={<User size={14} />} label='Created by' value={data?.created_by} />
                  </Col>
                  <Col xs={6} md={4}>
                    <MetaItem icon={<UserCheck size={14} />} label='Assigned to' value={data?.assigned_to} />
                  </Col>
                  <Col xs={6} md={4}>
                    <MetaItem icon={<Flag size={14} />} label='Priority' value={data?.priority} />
                  </Col>
                  <Col xs={6} md={4}>
                    <MetaItem icon={<Calendar size={14} />} label='Created' value={data?.created_at} />
                  </Col>
                  <Col xs={6} md={4}>
                    <MetaItem icon={<Clock size={14} />} label='Last updated' value={data?.last_updated_at} />
                  </Col>
                  <Col xs={6} md={4}>
                    <MetaItem icon={<AlertCircle size={14} />} label='Due date' value={data?.due_date} />
                  </Col>
                </Row>
              </div>

              {data?.description && (
                <div className='mb-4'>
                  <h6 className='fw-bold text-uppercase text-muted mb-2' style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                    Description
                  </h6>
                  <div
                    className='py-2 ps-3 text-dark'
                    style={{ borderLeft: '3px solid #0d6efd', lineHeight: 1.6, fontSize: 14 }}
                    dangerouslySetInnerHTML={{ __html: data?.description }}
                  />
                </div>
              )}

              {data?.attachments?.length > 0 && (
                <div className='mb-4'>
                  <h6 className='fw-bold text-uppercase text-muted mb-2' style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                    Attachments ({data.attachments.length})
                  </h6>
                  <div className='d-flex flex-wrap' style={{ gap: 8 }}>
                    {data.attachments.map(each => (
                      <a
                        key={each.filename}
                        href={each.download_url}
                        download
                        target='_blank'
                        rel='noreferrer'
                        className='d-inline-flex align-items-center text-decoration-none border rounded px-3 py-2 bg-white'
                        style={{ fontSize: 13, transition: 'border-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#0d6efd'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#dee2e6'}
                      >
                        <Paperclip size={14} className='text-primary me-2' />
                        <span className='text-dark me-2'>{each.filename}</span>
                        <span className='text-muted' style={{ fontSize: 12 }}>{each.file_size}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {data?.replies?.length > 0 && (
                <div className='mt-5'>
                  <h6 className='fw-bold text-uppercase text-muted mb-3' style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                    Activity ({data.replies.length})
                  </h6>
                  <div className='position-relative' style={{ paddingLeft: 44 }}>
                    <div
                      className='position-absolute'
                      style={{ left: 15, top: 4, bottom: 4, width: 2, background: '#e9ecef' }}
                    />
                    {data.replies.map((each) => {
                      const isChangeOnly = each?.changes?.length > 0;
                      return (
                        <div key={each.id} className='position-relative pb-4'>
                          <div className='position-absolute' style={{ left: -44, top: 0 }}>
                            <Avatar name={each.user_name} size={32} />
                          </div>
                          {isChangeOnly ? (
                            <div className='py-1'>
                              {each.changes.map((eachChange, index) => (
                                <div key={index} className='d-flex justify-content-between align-items-baseline text-muted mb-1' style={{ fontSize: 13 }}>
                                  <div dangerouslySetInnerHTML={{ __html: eachChange.changes_in_the_text }} />
                                  <span className='ms-2 text-nowrap' style={{ fontSize: 11 }}>
                                    {eachChange.time_ago_in_words}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className='d-flex justify-content-between align-items-baseline mb-1'>
                              <strong className='text-dark' style={{ fontSize: 14 }}>{each.user_name}</strong>
                              <span className='text-muted' style={{ fontSize: 11 }}>{each.time_ago_in_words}</span>
                            </div>
                          )}
                          {each?.notes !== "" && (
                            <div
                              className='p-3 mt-2 bg-light rounded text-dark'
                              style={{ lineHeight: 1.6, fontSize: 14 }}
                              dangerouslySetInnerHTML={{ __html: each.notes }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {canReply && (
            <Card className='mt-3 shadow-sm border-0'>
              <CardBody className='p-4'>
                <h6 className='fw-bold text-uppercase text-muted mb-3' style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                  Add reply
                </h6>
                <textarea
                  className='form-control mb-3'
                  rows='4'
                  placeholder='Type your reply…'
                  ref={replyText}
                />
                <div className='d-flex justify-content-between align-items-center flex-wrap' style={{ gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 250 }}>
                    <Dropzone
                      maxFiles={1}
                      maxSizeBytes={14680064}
                      accept="image/png"
                      onChangeStatus={handleChangeStatus}
                      multiple={false}
                      canCancel={true}
                      inputContent={
                        <span className='d-inline-flex align-items-center text-muted'>
                          <FileText size={14} className='me-2' /> Browse / Drop a PNG file
                        </span>
                      }
                      styles={{
                        dropzone: { height: 50, border: '1px dashed #ced4da', overflow: 'hidden' },
                        dropzoneActive: { borderColor: '#0d6efd' },
                      }}
                    />
                  </div>
                  <div className='d-flex' style={{ gap: 8 }}>
                    <button
                      className='btn btn-outline-primary'
                      disabled={!('token' in imageUpload)}
                      onClick={handleUpload}
                    >
                      Upload
                    </button>
                    <button className='btn btn-success d-inline-flex align-items-center' onClick={handleSubmit}>
                      <Send size={14} className='me-2' /> Submit
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </SpinnerLoader>
      </Container>
    </div>
  );
}
